import { CONFIG } from '../config/constants.js';

/**
 * Determines which ending the player achieved
 * Checks endings in priority order:
 * 1. Space
 * 2. Spiritual
 * 3. Conquest
 * 4. Prosperity
 * 5. Survival (default)
 *
 * @param {Object} state - Game state
 * @returns {Object|null} Ending object or null if game not ended
 */
export function determineEnding(state) {
  if (state.day < CONFIG.WIN_DAY) return null;

  const alive = state.workers.filter(w => w.status !== 'dead');

  // Defeat condition
  if (alive.length === 0) {
    return {
      type: 'defeat',
      icon: '💀',
      title: 'The Settlement Falls',
      message: `Your settlement fell on day ${state.day}. The wilderness claims another dream...`,
      color: 'text-red-400'
    };
  }

  // Check victory endings in priority order
  if (checkSpaceEnding(state)) return createEnding('space', state);
  if (checkSpiritualEnding(state)) return createEnding('spiritual', state);
  if (checkConquestEnding(state)) return createEnding('conquest', state);
  if (checkProsperityEnding(state)) return createEnding('prosperity', state);

  // Default survival ending
  return createEnding('survival', state);
}

/**
 * Checks if Space ending conditions are met
 * Requirements:
 * - Spaceport complete
 * - 100+ food stockpiled
 * - 50+ metal stockpiled
 * - 8+ living workers
 */
function checkSpaceEnding(state) {
  const hasSpaceport = state.buildings.some(b => b.type === 'spaceport' && b.complete);
  const alive = state.workers.filter(w => w.status !== 'dead').length;

  return (
    hasSpaceport &&
    state.food >= 100 &&
    (state.metal || 0) >= 50 &&
    alive >= 8
  );
}

/**
 * Checks if Spiritual ending conditions are met
 * Requirements:
 * - Temple complete
 * - 80+ knowledge
 * - 80+ morale
 * - No deaths in last 5 days
 */
function checkSpiritualEnding(state) {
  const hasTemple = state.buildings.some(b => b.type === 'temple' && b.complete);
  const recentDeaths = (state.deathLog || []).filter(
    deathInfo => deathInfo.day > state.day - 5
  );

  return (
    hasTemple &&
    (state.knowledge || 0) >= 80 &&
    (state.morale || 50) >= 80 &&
    recentDeaths.length === 0
  );
}

/**
 * Checks if Conquest ending conditions are met
 * Requirements:
 * - Fortress complete
 * - Barracks complete
 * - Wall complete
 * - 15+ metal stockpiled
 */
function checkConquestEnding(state) {
  const hasFortress = state.buildings.some(b => b.type === 'fortress' && b.complete);
  const hasBarracks = state.buildings.some(b => b.type === 'barracks' && b.complete);
  const hasWall = state.buildings.some(b => b.type === 'wall' && b.complete);

  return (
    hasFortress &&
    hasBarracks &&
    hasWall &&
    (state.metal || 0) >= 15
  );
}

/**
 * Checks if Prosperity ending conditions are met
 * Requirements:
 * - Monument complete
 * - 10+ living workers
 * - 90+ morale
 * - All utility buildings complete
 */
function checkProsperityEnding(state) {
  const hasMonument = state.buildings.some(b => b.type === 'monument' && b.complete);
  const alive = state.workers.filter(w => w.status !== 'dead').length;

  const requiredUtilityBuildings = ['farm', 'granary', 'library', 'hospital', 'marketplace'];
  const hasAllUtilities = requiredUtilityBuildings.every(type =>
    state.buildings.some(b => b.type === type && b.complete)
  );

  return (
    hasMonument &&
    alive >= 10 &&
    (state.morale || 50) >= 90 &&
    hasAllUtilities
  );
}

/**
 * Creates an ending object with narrative details
 */
function createEnding(type, state) {
  const endings = {
    space: {
      type: 'space',
      icon: '🚀',
      title: 'Escape to the Stars',
      message: `With ${state.workers.filter(w => w.status !== 'dead').length} brave souls aboard, your settlement launches into the cosmos. The stars await your new beginning...`,
      flavor: 'You achieved the Space Victory by building a spaceport and gathering the resources to leave this world behind.',
      color: 'text-blue-400'
    },
    spiritual: {
      type: 'spiritual',
      icon: '🕉️',
      title: 'Ascension',
      message: 'Through wisdom and inner peace, your people transcend mortal concerns. The temple glows with an otherworldly light...',
      flavor: 'You achieved the Spiritual Victory through knowledge, high morale, and peaceful existence.',
      color: 'text-purple-400'
    },
    conquest: {
      type: 'conquest',
      icon: '⚔️',
      title: 'Rise of Empire',
      message: 'Raiders bow before your might. Your fortress stands unconquered, a symbol of strength that will echo through the ages...',
      flavor: 'You achieved the Conquest Victory through military prowess and decisive victories in battle.',
      color: 'text-red-400'
    },
    prosperity: {
      type: 'prosperity',
      icon: '🌟',
      title: 'Golden Age',
      message: `Your thriving settlement of ${state.workers.filter(w => w.status !== 'dead').length} souls becomes a beacon of hope. Others journey from afar to join your prosperity...`,
      flavor: 'You achieved the Prosperity Victory through population growth, infrastructure, and exceptional morale.',
      color: 'text-yellow-400'
    },
    survival: {
      type: 'survival',
      icon: '🏕️',
      title: 'We Endure',
      message: `Against all odds, your settlement survives the ${CONFIG.WIN_DAY}-day trial with ${state.workers.filter(w => w.status !== 'dead').length} souls remaining. Tomorrow, you rebuild...`,
      flavor: `You achieved the Survival Victory by reaching day ${CONFIG.WIN_DAY} alive. Greater destinies await with more ambitious goals.`,
      color: 'text-green-400'
    }
  };

  return endings[type];
}

/**
 * Calculates progress toward each ending (for UI display on days 18+)
 * @param {Object} state - Game state
 * @returns {Object} Progress toward each ending
 */
export function calculateEndingProgress(state) {
  const alive = state.workers.filter(w => w.status !== 'dead').length;

  return {
    space: {
      met: checkSpaceEnding(state),
      requirements: [
        { text: 'Spaceport built', met: state.buildings.some(b => b.type === 'spaceport' && b.complete) },
        { text: '100 food stockpiled', met: state.food >= 100, current: state.food },
        { text: '50 metal stockpiled', met: (state.metal || 0) >= 50, current: state.metal || 0 },
        { text: '8+ living workers', met: alive >= 8, current: alive }
      ]
    },
    spiritual: {
      met: checkSpiritualEnding(state),
      requirements: [
        { text: 'Temple built', met: state.buildings.some(b => b.type === 'temple' && b.complete) },
        { text: '80+ knowledge', met: (state.knowledge || 0) >= 80, current: state.knowledge || 0 },
        { text: '80+ morale', met: (state.morale || 50) >= 80, current: state.morale || 50 },
        { text: 'No deaths in 5 days', met: (state.deathLog || []).filter(d => d.day > state.day - 5).length === 0 }
      ]
    },
    conquest: {
      met: checkConquestEnding(state),
      requirements: [
        { text: 'Fortress built', met: state.buildings.some(b => b.type === 'fortress' && b.complete) },
        { text: 'Barracks built', met: state.buildings.some(b => b.type === 'barracks' && b.complete) },
        { text: 'Wall built', met: state.buildings.some(b => b.type === 'wall' && b.complete) },
        { text: '15+ metal', met: (state.metal || 0) >= 15, current: state.metal || 0 }
      ]
    },
    prosperity: {
      met: checkProsperityEnding(state),
      requirements: [
        { text: 'Monument built', met: state.buildings.some(b => b.type === 'monument' && b.complete) },
        { text: '10+ living workers', met: alive >= 10, current: alive },
        { text: '90+ morale', met: (state.morale || 50) >= 90, current: state.morale || 50 },
        { text: 'All utility buildings', met: ['farm', 'granary', 'library', 'hospital', 'marketplace'].every(t =>
          state.buildings.some(b => b.type === t && b.complete)
        ) }
      ]
    }
  };
}
